import { useState, useEffect, ChangeEvent } from 'react';
import ReactModal, { Props as ReactModalProps } from 'react-modal';
import css from 'styled-jsx/css';
import classNames from 'classnames';
import EmailSubmitCard from 'components/ContentModal/EmailSubmitCard';
import EmailSubmitSuccessCard from 'components/ContentModal/EmailSubmitSuccessCard';
import contentPromoLarge from 'public/static/images/content-promo-large.svg';
import contentPromoSmall from 'public/static/images/content-promo-small.svg';
import closeX from 'public/static/images/promo-modal-close.svg';
import { screen, colors } from 'components/styles/variables';
import useEvents from 'lib/hooks/useEvents';
import modalViewEvent from 'lib/Tracking/ContentModal/ModalView';
import modalCloseEvent from 'lib/Tracking/ContentModal/ModalClose';
import closeTapEvent from 'lib/Tracking/ContentModal/CloseTap';
import emailSubmitTapEvent from 'lib/Tracking/ContentModal/EmailSubmitTap';
import { postEventToIterable } from 'lib/IterableApi';

export const CONTENT_MODAL_SHOWN_KEY = 'contentModalShown';
const CONTENT_UPSELL_SIGNUP = 'content_upsell_signup';

const openModalCSS = css.resolve`
  .ReactModal__Html--open {
    overflow-y: hidden;
  }
`;

const modalStyle: ReactModalProps['style'] = {
  overlay: {
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  content: {
    background: '#fff',
    WebkitOverflowScrolling: 'touch',
    borderRadius: '16px',
    padding: '0',
    outline: 'none',
  },
};

const modalCss = css.resolve``;

interface ContentModalProps {
  showModal: boolean;
}

export default function ContentModal({ showModal }: ContentModalProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(showModal);
  const [email, setEmail] = useState('');
  const logger = useEvents();

  useEffect(() => {
    setIsOpen(showModal);
  }, [showModal]);

  const [isSubmitted, setIsSubmitted] = useState(false);

  const onClose = (): void => {
    setIsOpen(false);
    logger.log(modalCloseEvent(window.location.href));
    logger.log(closeTapEvent(window.location.href));
  };

  return (
    <>
      <ReactModal
        style={modalStyle}
        className={classNames(modalCss.className)}
        isOpen={isOpen}
        onRequestClose={onClose}
        onAfterOpen={(): void => {
          logger.log(modalViewEvent(window.location.href));
        }}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        htmlOpenClassName={`${openModalCSS.className} ReactModal__Html--open`}
      >
        <div className="content-modal">
          <ModalCloseButton onClose={onClose} />
          <div className="card-image">
            <img src={contentPromoLarge} alt="large promo image" />
          </div>
          <div className="mobile-image">
            <img src={contentPromoSmall} alt="small promo image" />
          </div>
          {!isSubmitted ? (
            <EmailSubmitCard
              onSubmit={(): void => {
                setIsSubmitted(true);
                logger.log(emailSubmitTapEvent(window.location.href));
                postEventToIterable({ email, eventName: CONTENT_UPSELL_SIGNUP });
              }}
              onEmailFieldChange={(e: ChangeEvent<HTMLInputElement>): void =>
                setEmail(e.target.value)
              }
            />
          ) : (
            <EmailSubmitSuccessCard email={email} />
          )}
        </div>
      </ReactModal>
      <style jsx>
        {`
          .content-modal {
            display: flex;
            width: 304px;
            height: 328px;
            position: relative;
            border-radius: 16px;
          }

          .card-image img {
            display: none;
          }

          .mobile-image {
            position: absolute;
            left: 107px;
            top: -45px;
          }

          .mobile-image img {
            filter: drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.08));
            border-radius: 16px;
          }

          @media (min-width: ${screen.smMin}) {
            .content-modal {
              display: flex;
              width: 736px;
              height: 340px;
              overflow: hidden;
            }

            .card-image img {
              display: block;
              width: 368px;
            }

            .mobile-image {
              display: none;
            }
          }
        `}
      </style>
      {openModalCSS.styles}
    </>
  );
}

interface ModalCloseButtonProps {
  onClose: () => void;
}

function ModalCloseButton({ onClose }: ModalCloseButtonProps): JSX.Element {
  return (
    <button aria-label="close" className="close-button" onClick={onClose}>
      <img src={closeX} alt="close button" />
      <style jsx>{`
        .close-button {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          top: 12px;
          right: 12px;
          height: 28px;
          width: 28px;
          cursor: pointer;
          padding: 0;
          border: none;
          background-color: ${colors.WHITE};
        }

        .close-button:hover {
          background-color: ${colors.G1};
          border-radius: 4px;
        }
      `}</style>
    </button>
  );
}
